import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { guardScreen, screenGuardMode } from '../../utils/screenGuard';
import StoryVideoPlayer from '../media/StoryVideoPlayer';
import { BODY_FONT } from './messagesModel';
import { clipItem } from './VideoMessage';
import usePageScrollLock from '../../utils/usePageScrollLock';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';

/**
 * The one look at a view-once message, full screen on black.
 *
 * The content arrived with the open call and lives only in this component's props; when it
 * unmounts the words are gone from the page, and the server has already forgotten them. What
 * the viewer does about screenshots depends on what the device can do (screenGuard.js):
 *
 * - block (Android): the window is secure for the length of the look — a screenshot comes out
 *   black. Tap to open, CLOSE to finish.
 * - detect (iOS): a screenshot can't be stopped, so it is REPORTED — the sender gets a line in
 *   the thread — and while the screen is being recorded or mirrored the content is hidden.
 * - none (a browser): hold-to-view. The content shows while a finger or a mouse button is
 *   down on it and hides the moment it lifts, so it never sits readable with both hands free.
 *   Not a guarantee — nothing in a browser is — but it is the honest deterrent.
 */
export default function ViewOnceViewer({ msg, senderName, onClose, onScreenshot }) {
  const mode = screenGuardMode();
  const hold = mode === 'none';
  const [held, setHeld] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [shot, setShot] = useState(false);
  const release = useRef(null);

  useEffect(() => {
    let alive = true;
    guardScreen({
      onScreenshot: () => { if (!alive) return; setShot(true); onScreenshot?.(); },
      onCaptureChanged: (c) => { if (alive) setCaptured(c); },
    }).then((r) => { if (alive) release.current = r; else r(); });
    return () => { alive = false; release.current?.(); release.current = null; };
  }, [onScreenshot]);

  // Scroll stays where it was under the sheet; the keyboard can't be up over it.
  usePageScrollLock(true);

  // Swipe down to close. Deliberately stiffer than the other viewers: on the web this is a
  // hold-to-view surface, and closing SPENDS the look — a finger that drifts while holding
  // must not cost someone their one look.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => onClose?.(), distance: 180, velocity: 1.1 });

  // A hidden tab, a lost window or Escape ends the hold; Escape also closes.
  useEffect(() => {
    const drop = () => setHeld(false);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('blur', drop);
    document.addEventListener('visibilitychange', drop);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('blur', drop); document.removeEventListener('visibilitychange', drop); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const press = useCallback((e) => { if (e.button === undefined || e.button === 0) setHeld(true); }, []);
  const lift = useCallback(() => setHeld(false), []);
  const showing = !captured && (!hold || held);
  const isImage = msg.kind === 'image';
  const isVideo = msg.kind === 'video';

  const node = (
    <div
      ref={frameRef}
      className="pr-overlay-root"
      data-testid="view-once-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="View once"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: '#000', color: '#fff', display: 'flex', flexDirection: 'column',
        userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none',
        paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
        ...swipe.style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', flexShrink: 0 }}>
        <div>
          <div className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.16em' }}>VIEW ONCE</div>
          {senderName && <div className="font-mono" style={{ fontSize: 10, letterSpacing: '.1em', opacity: 0.6, marginTop: 2 }}>FROM {senderName.toUpperCase()}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="font-mono font-bold"
          style={{ height: 36, padding: '0 16px', borderRadius: 999, border: '2px solid #fff', background: 'transparent', color: '#fff', fontSize: 10, letterSpacing: '.14em', cursor: 'pointer' }}
        >
          {hold ? 'DONE' : 'CLOSE'}
        </button>
      </div>

      <div
        data-testid="view-once-surface"
        onPointerDown={hold ? press : undefined}
        onPointerUp={hold ? lift : undefined}
        onPointerCancel={hold ? lift : undefined}
        onPointerLeave={hold ? lift : undefined}
        style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', cursor: hold ? 'pointer' : 'default' }}
      >
        {showing ? (
          isVideo ? (
            <div style={{ position: 'absolute', inset: 24 }}>
              {/* A view-once clip plays through once: forward taps on the ramp, no rewind, no scrub bar. */}
              <StoryVideoPlayer item={clipItem(msg)} active muted={false} fit="contain" seekable="forward" edgeZones={false} tapZones={!hold} tips={false} systemBarInset={false} />
            </div>
          ) : isImage ? (
            <img
              src={msg.imageUrl}
              alt=""
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 16, pointerEvents: 'none', WebkitTouchCallout: 'none', WebkitUserDrag: 'none' }}
            />
          ) : (
            <div data-testid="view-once-text" style={{ fontFamily: BODY_FONT, fontSize: 24, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 560, textAlign: 'center', pointerEvents: 'none' }}>
              {msg.body}
            </div>
          )
        ) : (
          <div className="font-mono font-bold" style={{ textAlign: 'center', fontSize: 11, letterSpacing: '.16em', opacity: 0.7, lineHeight: 1.8, pointerEvents: 'none' }}>
            {captured ? <>SCREEN RECORDING IS ON<br />HIDDEN UNTIL IT STOPS</> : <>HOLD TO VIEW<br /><span style={{ opacity: 0.6 }}>LET GO AND IT HIDES</span></>}
          </div>
        )}
      </div>

      <div className="font-mono" style={{ textAlign: 'center', padding: '12px 16px', fontSize: 9, letterSpacing: '.14em', opacity: 0.55, flexShrink: 0 }}>
        {shot ? 'SCREENSHOT TAKEN · THEY HAVE BEEN TOLD'
          : mode === 'block' ? 'SCREENSHOTS ARE BLOCKED WHILE THIS IS OPEN'
            : mode === 'detect' ? 'A SCREENSHOT TELLS THE SENDER'
              : 'SEEN ONE TIME · GONE WHEN YOU’RE DONE'}
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
